import *  as React from 'react';
import { useState, useEffect } from 'react';
import sidebarData from "../data/sidebarData";
import { AnimatePresence, motion } from "framer-motion"
import { useAtom } from 'jotai'
import { imgMove, sceneAtom, currentUrl, selectedName } from "../data/AtomData"
import { useNavigate, useParams } from 'react-router-dom';
import { InlineShareButtons } from 'sharethis-reactjs';



interface SidebarProps { }

const framerSidebarPanel = {
	initial: { x: '-100%' },
	animate: { x: 0 },
	exit: { x: '-100%' },
	transition: { duration: 0.2 },
} 

const framerChildSidebarPanel = {
	initial: { x: '100%' },
	animate: { x: 0 },
	exit: { x: '100%' },
	leave: { x: '100%' },
	transition: { duration: 0.2 },
}

const extractImagePaths = (scene: { [index: string]: string | string[] }): string[] => {
	return Object.values(scene).flat();
};


const defaultConfig = {
	baseCabs: 'bc1',
	wallCabs: 'wc1',
	workSurface: 'ws1',
	handles: 'h1',
	floor: 'f1',
	wall: 'w1',
};

const textToConfigKey: Record<string, keyof typeof defaultConfig> = {
	"Base Cabinet Fronts": "baseCabs",
	"Wall Cabinet Fronts": "wallCabs",
	"Worktop": "workSurface",
	"Handle Options": "handles",
	"Flooring Style": "floor",
	"Wall Finish": "wall",
};


const parseConfig = (configString: string) => {
	const config = { ...defaultConfig };

	// Match patterns and assign to the correct part of the configuration
	const regex = /(bc\d{1,2})|(wc\d{1,2})|(ws\d{1,2})|(h\d{1,2})|(f\d{1,2})|(w\d{1,2})/g;
	const matches = configString.match(regex) || [];

	// Create a mapping from regex matches to configuration keys
	const configKeys = Object.keys(defaultConfig) as Array<keyof typeof defaultConfig>;

	matches.forEach((match, index) => {
		// Determine which part matched and update the config accordingly
		const key = configKeys[index];
		if (key) {
			config[key] = match;
		}
	});

	return config;
};

const useInitializeUrl = () => {
	const [, setCurrentUrl] = useAtom(currentUrl);

	useEffect(() => {
		const urlPath = window.location.pathname.slice(1); // Remove the leading slash
		const parsedUrl = parseConfig(urlPath);
		setCurrentUrl(parsedUrl);
	}, [setCurrentUrl]);
};

export const Sidebar: React.FC<SidebarProps> = (props: React.PropsWithChildren<SidebarProps>) => {
	const [selectedBlock, setSelectedBlock] = useState<number | null>(null);
	const [listType, setListType] = useState<string | undefined>("");
	const [groupList, setGroupList] = useState<boolean | undefined>(false);
	const [itemNum, setItemNum] = useState(-1);
	const [childItemNum, setChildItemNum] = useState(-1);
	const [openSidebar, setOpenSidebar] = useState(false);
	const [openChildSidebar, setOpenChildSidebar] = useState(false);
	const [splitNum, setSplitNum] = useState(0);
	const [moveImg, setMoveImg] = useAtom(imgMove);
	const [allImgs, setAllImgs] = useAtom(sceneAtom);
	const [currentNames, setCurrentNames] = useAtom(selectedName);


	useInitializeUrl();

	const [isSmallScreen] = useState(window.innerWidth <= 700);

	useEffect(() => {
		if (isSmallScreen) {
			setItemNum(0);
			setSelectedBlock(0);
			setOpenSidebar(true);
			setMoveImg(!openSidebar);
			setOpenChildSidebar(false);
			setListType("medium");
		}
	}, []);

	const handleClick = (ix: number) => {
		setItemNum(ix);
		if (selectedBlock === null) {
			!isSmallScreen && (setOpenSidebar(!openSidebar));
			setSelectedBlock(selectedBlock === ix ? selectedBlock : ix);
		}
		else if (selectedBlock === ix) {
			!isSmallScreen && (setOpenSidebar(!openSidebar));
		}
		else {
			setSelectedBlock(selectedBlock === ix ? selectedBlock : ix);
			!openSidebar && (!isSmallScreen && (setOpenSidebar(!openSidebar)));
		}
		setMoveImg(!openSidebar);
		setOpenChildSidebar(false);
	};

	const ChildMenuClick = (iq: number) => {
		setChildItemNum(iq);
		//	setOpenChildSidebar(!openChildSidebar);
	};

	const MenuBack = () => {
		setOpenChildSidebar(false);
	};

	const [productNames, setProductNames] = useState<string[]>(["Bordeaux", "Bordeaux", "Brillaint White", "No Handle"]);

	const ImageChange = (key: string, [bigImg, text, menuImg]: string[]) => {
		const keyToIndexMap: { [key: string]: number } = {
			"Base Cabinet Fronts": 0,
			"Wall Cabinet Fronts": 1,
			"Worktop": 2,
			"Handle Options": 3,
		};

		if (key in keyToIndexMap) {
			const newNames = [...productNames];
			newNames[keyToIndexMap[key]] = text.toString();
			setProductNames(newNames);
		}
		setAllImgs(prevState => ({
			...prevState,
			[key]: [bigImg]
		}));
	};

	const SplitChange = (num: number) => {
		setSplitNum(num);
	};

	const [imagePaths, setImagePaths] = useState<string[]>([]);

	useEffect(() => {
		const paths = extractImagePaths(allImgs);
		setImagePaths(paths);
	}, [allImgs]);

	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (isLoading) {
				const confirmationMessage = 'Are you sure you want to leave? Your data might not be saved.';
				event.returnValue = confirmationMessage;
				return confirmationMessage;
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [isLoading]);

	//URL PART

	const navigate = useNavigate();
	const { config } = useParams<{ config?: string }>();
	const initialConfig = config ? parseConfig(config) : defaultConfig;
	const [selectedConfig, setSelectedConfig] = React.useState(initialConfig);
	const [urlChange, setUrlChange] = useAtom(currentUrl);


	React.useEffect(() => {
		if (config) {
			const parsedConfig = parseConfig(config);
			setSelectedConfig(parsedConfig);
		}
	}, [config]);

	const updateConfigPart = (part: keyof typeof defaultConfig, value: string) => {
		const newConfig = { ...selectedConfig, [part]: value };

		const configString = [
			newConfig.baseCabs || '',
			newConfig.wallCabs || '',
			newConfig.workSurface || '',
			newConfig.handles || '',
			newConfig.floor || '',
			newConfig.wall || '',
		].join('');

		setSelectedConfig(newConfig);

		setUrlChange(newConfig);

		navigate(`/${configString}`, { replace: true });
	};

	const handleItemClick = (part: string, newValue: string) => {
		const configKey = textToConfigKey[part];
		if (configKey) {
			updateConfigPart(configKey, newValue);
		} else {
			console.error(`No mapping found for part: ${part}`);
		}
	};

	const [webproductNames, setWebproductNames] = useState<string[]>(currentNames);

	useEffect(() => {
		setWebproductNames(currentNames);
	}, [currentNames]);

	const handleDownload = async () => {
		setIsLoading(true);
		try {
			const response = await fetch('/api/image/upload', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ ImagePaths: imagePaths, ProductNames: webproductNames }),
			});

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.style.display = 'none';
			a.href = url;
			a.download = 'Unity_Image.png';
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error downloading image:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const wholeUrl = window.location.href;

	const shareOnTwitter = () => {
		window.open(
			`https://twitter.com/intent/tweet?url=${encodeURIComponent(wholeUrl)}`,
			'_blank'
		);
	};

	const shareOnFacebook = () => {
		window.open(
			`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(wholeUrl)}`,
			'_blank'
		);
	};

	const shareOnLinkedIn = () => {
		window.open(
			`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(wholeUrl)}`,
			'_blank'
		);
	};

	const shareOnPinterest = () => {
		window.open(
			`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(wholeUrl)}`,
			'_blank'
		);
	};

	const shareViaEmail = () => {
		window.location.href = `mailto:?subject=Check out my new kitchen&body=Made with the unity configurator: ${encodeURIComponent(wholeUrl)}`;
	};

	return (<>
		<div className="sidebar">
			{sidebarData.map((block, ix) => (
				<div className={`sidebar-block${block.imgTxtOnly || block.textPosition === "menu" || block.textPosition === "side-menu" ? ' img-only' : ''}${selectedBlock === ix && openSidebar ? " selected" : ""}${block.imgTxtOnly === true ? " txt-img-only" : ""}`}
					key={block.id} onClick={block.imgTxtOnly ? () => { } : () => { handleClick(ix); setListType(block.listType); setGroupList(block.groupList) }}
				>

					{block.imgCode === "" ? (<>
						<h1 className="unity-header">{block.text}</h1>
					</>) : (<>
						{block.fontAwesome ?
							<i className={`${block.imgCode}`}></i> : (<>
								{selectedBlock === ix && openSidebar ?
									<img className={`${block.imgTxtOnly ? '' : 'icon-image'}`} src={require(`../img/${block.imgCode}-selected.png`)} alt={`${block.text} icon`} />
									:
									<img className={`${block.imgTxtOnly ? '' : 'icon-image'}`} src={require(`../img/${block.imgCode}.png`)} alt={`${block.text} icon`} />
								}
							</>)}

						{block.textPosition === "side" || block.textPosition === "side-menu" ? <p>{block.text}</p> : <></>}
					</>)}

				</div>
			))}
		</div>
		<AnimatePresence mode="wait" initial={false}>
			{openSidebar && (<>
				<motion.div
					className={`open-sidebar ${openChildSidebar ? "" : listType}`}
					initial={isSmallScreen ? undefined : framerSidebarPanel.initial}
					animate={isSmallScreen ? undefined : framerSidebarPanel.animate}
					exit={isSmallScreen ? undefined : framerSidebarPanel.exit}
					transition={framerSidebarPanel.transition}
					aria-label="Sidebar"
					key={itemNum}
				>
					<div className="close-side" onClick={() => { handleClick(itemNum) }}>
						<i className="fas fa-times"></i>
					</div>

					<div className={`open-sidebar-column${sidebarData[itemNum].splitCategory != null ? " split-column" : ""}`}  >
						<>
							{sidebarData[itemNum].splitCategory != null ?
								(<>
									<div className="split-change">
										{sidebarData[itemNum]?.splitCategory?.map((splitCat, i) => (
											<>
												<div className="split-change-image" onClick={() => { SplitChange(i) }}>
													{i === splitNum ?
														<img src={require(`../img/${splitCat.imgCode}-selected.png`)} alt="" />
														:
														<img src={require(`../img/${splitCat.imgCode}.png`)} alt="" />
													}
													<p>{splitCat.text}</p>
												</div>
											</>
										))}
									</div>
									<h2>{sidebarData[itemNum]?.splitCategory?.[splitNum]?.text}</h2>

								</>)
								:
								<h2>{sidebarData[itemNum].text}</h2>
							}

							{sidebarData[itemNum].splitCategory != null ?
								(<>
									{sidebarData[itemNum]?.splitCategory?.[splitNum]?.subMenu?.map((newside, iq) => (<>
										<div className="open-sidebar-single" key={iq} onClick={newside.sideCode === "" && newside.mainImg === "" ? () => { } :
											() => newside.childSubMenu != null ? ChildMenuClick(iq) : null}>
											{listType === "checkbox" ? (
												<>
													{newside.childSubMenu != null ? (<>
														<div className="open-sidebar-single-child-title ">
															<p>{newside.text}</p>
														</div>

														{newside.childSubMenu.map((child, index) => (
															<div key={index} className="open-sidebar-single-child"
																onClick={() => {
																	const splitCategory = sidebarData[itemNum]?.splitCategory;
																	const text = splitCategory ? splitCategory[splitNum]?.text : undefined;
																	if (text) { ImageChange(text, [child.mainImg, child.text]); }
																}} >
																{child.text}
																<input
																	type="checkbox"
																	checked={(() => {
																		const splitCategory = sidebarData[itemNum]?.splitCategory;
																		const text = splitCategory ? splitCategory[splitNum]?.text : undefined;
																		return text ? child.mainImg === allImgs[text]?.[0] : false;
																	})()}
																/>
																<span className="checkmark"></span>
															</div>
														))}
													</>) : (
														<div className="open-sidebar-single-child"
															onClick={() => {
																const splitCategory = sidebarData[itemNum]?.splitCategory;
																const text = splitCategory ? splitCategory[splitNum]?.text : undefined;
																if (text) { ImageChange(text, [newside.mainImg, newside.text]); }
															}} >
															<p>{newside.text}</p>
														</div>
													)
													}
												</>
											) : (
												<>
													{listType === "medium" ? (
														<>
															{newside.childSubMenu != null ? (<>
																<div className="open-sidebar-single-child-title ">
																	<p>{newside.text}</p>
																</div>

																<div className="open-sidebar-child-row">
																	{newside.childSubMenu.map((child, index) => (
																		<div key={index} className={`open-sidebar-single ${child.mainImg === allImgs[(sidebarData[itemNum]?.splitCategory?.[splitNum]?.text ?? 'Base Cabinet Fronts')][0] ? "selected" : ""}`}
																			onClick={() => {
																				const splitCategory = sidebarData[itemNum]?.splitCategory;
																				const text = splitCategory ? splitCategory[splitNum]?.text : undefined;
																				if (text) {
																					child.webCode
																						? handleItemClick(text, child.webCode)
																						: console.error(`webCode is undefined for ${child.text}`);
																					/*ImageChange(text, [child.mainImg, child.text]);*/
																				}
																			}} >

																			<div className="open-sidebar-single-child">
																				<img src={require(`../img/${child.sideImg}`)} alt={newside.text} />
																				<p>{child.text}</p>
																			</div>
																		</div>
																	))}
																</div>
															</>) : (
																<div className="open-sidebar-single-child"
																	onClick={() => {
																		const splitCategory = sidebarData[itemNum]?.splitCategory;
																		const text = splitCategory ? splitCategory[splitNum]?.text : undefined;
																		if (text) {
																			newside.webCode
																				? handleItemClick(text, newside.webCode)
																				: console.error(`webCode is undefined for ${newside.text}`);
																			/*ImageChange(text, [newside.mainImg, newside.text]);*/
																		}
																	}} >
																	<p>{newside.text}</p>
																</div>
															)
															}
														</>
													) : (
														<div className="open-sidebar-single-child">
															{newside.fontAwesome ?
																<i className={newside.sideCode}></i> :
																<img src={require(`../img/${newside.sideCode}.jpg`)} alt={newside.text} />
															}
															<p>{newside.text}</p>
														</div>
													)}
												</>
											)}
										</div>
									</>))}
								</>) :
								(<>
									{(groupList === true) ? (<>
										{sidebarData[itemNum].subMenu.map((newside, iq) => (<>
											<div className={`open-sidebar-single${newside.childSubMenu != null ? "" : " smaller"}${listType === "small" ? " hoverable" : ""}`} key={iq}
												onClick={(newside.sideCode === "" && newside.mainImg === "") ?
													() => { } :
													() => {
														if (newside.text === "Download Image") {
															handleDownload();
														}
														if (newside.childSubMenu != null) {
															ChildMenuClick(iq);
														}
													}
												}>
												{listType === "checkbox" ? (
													<>
														{newside.childSubMenu != null ? (<>

															{(newside.sideCode !== null && newside.sideCode !== undefined && newside.sideCode !== "") && (<>
																<div className="open-sidebar-single-child-top-img">
																	<img src={require(`../img/${newside.sideCode}`)} alt="" />
																</div>
															</>)}

															<div className="open-sidebar-single-child-title ">
																<p>{newside.text}</p>
															</div>

															<div className="open-sidebar-single-child-container" style={newside.childSubMenu[0].text === "No Handle" ? { display: "flex", justifyContent: "center" } : {}}>
																{newside.childSubMenu.map((child, index) => (
																	<div key={index} className="open-sidebar-single-child"
																		onClick={() => {

																			child.webCode
																				? handleItemClick(sidebarData[itemNum].text, child.webCode)
																				: console.error(`webCode is undefined for ${child.text}`);

																			ImageChange(sidebarData[itemNum].text, [child.mainImg, child.text]);
																		}}>
																		{child.text} <input type="checkbox" checked={child.mainImg === allImgs[sidebarData[itemNum].text][0] ? true : false} /> <span className="checkmark"></span>
																	</div>
																))}
															</div>

														</>) : (
															<div className="open-sidebar-single-child smaller"
																onClick={() => {

																	newside.webCode
																		? handleItemClick(sidebarData[itemNum].text, newside.webCode)
																		: console.error(`webCode is undefined for ${newside.text}`);

																	ImageChange(sidebarData[itemNum].text, [newside.mainImg, newside.text])
																}}>
																{newside.text} <input type="checkbox" checked={newside.mainImg === allImgs[sidebarData[itemNum].text][0] ? true : false} /> <span className="checkmark" ></span>
															</div>
														)}
													</>
												) : (
													<>
														{listType === "medium" ? (
															<>
																{newside.childSubMenu !== null && newside.childSubMenu !== undefined ? (<>
																	<div className="open-sidebar-single-child-title ">
																		<p>{newside.text}</p>
																	</div>

																	<div className="open-sidebar-child-row">
																		{newside.childSubMenu.map((child, index) => (
																			<div key={index} className={`open-sidebar-single ${newside.mainImg === allImgs[sidebarData[itemNum].text][0] ? "selected" : ""}`}
																				onClick={() => ImageChange(sidebarData[itemNum].text, [child.mainImg, child.text])}
																			>
																				<div className="open-sidebar-single-child">
																					<img src={require(`../img/${child.sideImg}`)} alt={newside.text} />
																					<p>{child.text}</p>
																				</div>
																			</div>
																		))}
																	</div>
																</>) : (<>
																</>)}
															</>
														) : (
															<div className="open-sidebar-single-child">
																{newside.fontAwesome ?
																	<i className={newside.sideCode}></i> :
																	<img src={require(`../img/${newside.sideCode}.jpg`)} alt={newside.text} />
																}
																<p>{newside.text}</p>
															</div>
														)}
													</>
												)}
											</div>
										</>))}
									</>) : (<></>)}

									{(listType === "medium") ? (<div className="open-sidebar-child-row">
										{sidebarData[itemNum].subMenu.map((newside, iq) => (<>
											{newside.childSubMenu === null || newside.childSubMenu === undefined ? (<>
												<div className={`open-sidebar-single ${newside.mainImg === allImgs[sidebarData[itemNum].text][0] ? "selected" : ""}`}
													key={iq} onClick={(newside.sideCode === "" && newside.mainImg === "") ? () => { } : () => {
														if (newside.text === "Download Image") {
															handleDownload();
														}
														if (newside.childSubMenu != null) {
															ChildMenuClick(iq);
														}
													}}
												>
													<div className="open-sidebar-single-child"
														onClick={() => {
															const partToUpdate = sidebarData[itemNum].text;
															newside.webCode
																? handleItemClick(partToUpdate, newside.webCode)
																: console.error(`webCode is undefined for ${newside.text}`);
															/*ImageChange(sidebarData[itemNum].text, [newside.mainImg, newside.text]);*/
														}}
													>
														<img src={require(`../img/${newside.sideCode}`)} alt={newside.text} />
														<p>{newside.text}</p>
													</div>
												</div>
											</>) : (<></>)}
										</>))}
									</div>) : (<></>)}

									{listType === "small" ? (<>
										{sidebarData[itemNum].subMenu.map((newside, iq) => (<>
											<div className={`open-sidebar-single${newside.childSubMenu != null ? "" : " smaller"}${listType === "small" ? " hoverable" : ""}`} key={iq}
												onClick={(newside.sideCode === "" && newside.mainImg === "") ?
													() => { } :
													() => {
														if (newside.text === "Download Image") {
															handleDownload();
														}
														if (newside.childSubMenu != null) {
															ChildMenuClick(iq);
														}
													}
												}>
												<div className="open-sidebar-single-child">
													{newside.fontAwesome ?
														<i className={newside.sideCode}></i> :
														<img src={require(`../img/${newside.sideCode}.jpg`)} alt={newside.text} />
													}
													<p>{newside.text}</p>
												</div>


											</div>

											{sidebarData[itemNum].text === "Export" ?
												<>
													<div className="open-sidebar-single smaller hoverable" onClick={shareViaEmail}>
														<div className="open-sidebar-single-child">
															<i className="fas fa-envelope fa-lg fa-fw"></i>
															<p>Email</p>
														</div>
													</div>

													<div className="open-sidebar-single smaller hoverable" onClick={shareOnFacebook}>
														<div className="open-sidebar-single-child">
															<i className="fab fa-facebook-square fa-lg fa-fw"></i>
															<p>Facebook</p>
														</div>
													</div>

													<div className="open-sidebar-single smaller hoverable" onClick={shareOnTwitter}>
														<div className="open-sidebar-single-child">
															<i className="fab fa-twitter-square fa-lg fa-fw"></i>
															<p>Twitter</p>
														</div>
													</div>

													<div className="open-sidebar-single smaller hoverable" onClick={shareOnPinterest}>
														<div className="open-sidebar-single-child">
															<i className="fab fa-pinterest-square fa-lg fa-fw"></i>
															<p>Pinterest</p>
														</div>
													</div>

													<div className="open-sidebar-single smaller hoverable" onClick={shareOnLinkedIn}>
														<div className="open-sidebar-single-child">
															<i className="fab fa-linkedin fa-lg fa-fw"></i>
															<p>LinkedIn</p>
														</div>
													</div>
												</>
												: <></>
											}
										</>))}
									</>) : (<></>)}

								</>)
							}
						</>
					</div>
				</motion.div>
			</>)}
		</AnimatePresence>
		{isLoading && <div className="download-loading">
			<i className="fas fa-circle-notch fa-spin"></i>
		</div>
		}
	</>);
}